import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useMutation, useLazyQuery } from '@apollo/client/react';
import moment from 'moment';
import { Notify } from 'notiflix';

import { REGISTER_USER, LOGIN_USER } from 'helper/graphql/mutations';
import { GET_USER_DETAILS } from 'helper/graphql/queries';
import { getUserGUID, setSession } from 'utils/jwt';
import useAuth from 'hooks/useAuth';

const formatTypeLabel = (eventType) => {
	if (!eventType) return 'Event';
	return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const generateRandomPassword = () =>
	Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-4).toUpperCase();

// ─── Step 1: Auth form (same pattern as course enrollment) ─────────────────
const AuthStep = ({ onAuthenticated }) => {
	const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '' });
	const [isUserRegistered, setIsUserRegistered] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	const [onLoginUser] = useMutation(LOGIN_USER);
	const [onRegisterUser] = useMutation(REGISTER_USER);
	const [getUserInfo] = useLazyQuery(GET_USER_DETAILS);

	const handleChange = (e) =>
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

	const loginAndBootstrap = async (email, password) => {
		const res = await onLoginUser({ variables: { username: email, password } });
		if (res.errors) throw new Error('Login failed');

		const token = res.data.loginUser.token;
		setSession(token);
		localStorage.setItem('isAuthenticated', 'true');
		localStorage.setItem('accessToken', token);

		await getUserInfo({
			variables: { guid: getUserGUID(token) },
			onCompleted: (data) => {
				localStorage.setItem('user', JSON.stringify(data?.allUsers?.edges[0]?.node || ''));
				onAuthenticated();
			},
		});
	};

	const handleCheckEmail = async () => {
		setIsProcessing(true);
		try {
			// Reuse the public profile check the same way CourseSingle2 does
			const res = await getUserInfo({ variables: { email: formData.email } });
			const userExists = !!(res?.data?.allUsers?.edges?.length > 0);

			if (userExists) {
				setIsUserRegistered(true);
				return;
			}

			// New user — register then auto-login
			const password = generateRandomPassword();
			const registerRes = await onRegisterUser({
				variables: {
					email: formData.email,
					firstName: formData.firstName,
					lastName: formData.lastName,
					password,
					isPrivacyPolicyGiven: true,
					isConsentGiven: true,
				},
			});

			if (!registerRes.data?.createUser?.user) throw new Error('Registration failed');
			await loginAndBootstrap(formData.email, password);
		} catch (err) {
			Notify.failure(err?.message || 'Something went wrong. Please try again.');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleLoginExisting = async () => {
		if (!formData.password) return;
		setIsProcessing(true);
		try {
			await loginAndBootstrap(formData.email, formData.password);
		} catch {
			Notify.failure('Login failed. Please check your credentials.');
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<div className="enrollment-step">
			<div className="step-header mb-4 text-center">
				<div className="step-icon-wrapper mx-auto mb-3">
					<i className="fe fe-user step-icon"></i>
				</div>
				<h4 className="step-title">
					{isUserRegistered ? 'Welcome back!' : 'Join to Register'}
				</h4>
				<p className="step-subtitle text-muted">
					{isUserRegistered
						? 'Enter your password to continue'
						: 'Create a free account to register for this event'}
				</p>
			</div>

			<Form>
				{!isUserRegistered && (
					<Row className="g-3 mb-3">
						<Col md={6}>
							<Form.Group>
								<Form.Label className="form-label-modern">
									First Name <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									name="firstName"
									value={formData.firstName}
									onChange={handleChange}
									placeholder="John"
									className="form-control-modern"
								/>
							</Form.Group>
						</Col>
						<Col md={6}>
							<Form.Group>
								<Form.Label className="form-label-modern">
									Last Name <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									name="lastName"
									value={formData.lastName}
									onChange={handleChange}
									placeholder="Doe"
									className="form-control-modern"
								/>
							</Form.Group>
						</Col>
					</Row>
				)}

				<Form.Group className="mb-3">
					<Form.Label className="form-label-modern">
						Email Address <span className="text-danger">*</span>
					</Form.Label>
					<Form.Control
						type="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						placeholder="john.doe@example.com"
						className="form-control-modern"
						disabled={isUserRegistered}
					/>
					{isUserRegistered && (
						<Form.Text className="text-muted d-flex align-items-center mt-2">
							<i className="fe fe-check-circle text-success me-2"></i>
							Account found! Enter your password to continue.
						</Form.Text>
					)}
				</Form.Group>

				{isUserRegistered && (
					<Form.Group className="mb-4">
						<Form.Label className="form-label-modern">
							Password <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							type="password"
							name="password"
							value={formData.password}
							onChange={handleChange}
							placeholder="Enter your password"
							className="form-control-modern"
						/>
					</Form.Group>
				)}

				{!isUserRegistered && (
					<div className="privacy-notice mb-4">
						<i className="fe fe-shield text-success me-2"></i>
						<small className="text-muted">
							By continuing you agree to our Terms of Service and Privacy Policy.
						</small>
					</div>
				)}

				<Button
					className="btn-enrollment-primary w-100"
					disabled={
						isProcessing ||
						!formData.email ||
						(!isUserRegistered && (!formData.firstName || !formData.lastName))
					}
					onClick={isUserRegistered ? handleLoginExisting : handleCheckEmail}
				>
					{isProcessing ? (
						<>
							<span className="spinner-border spinner-border-sm me-2"></span>
							Processing...
						</>
					) : isUserRegistered ? (
						<><i className="fe fe-log-in me-2"></i>Login &amp; Continue</>
					) : (
						<><i className="fe fe-arrow-right me-2"></i>Continue</>
					)}
				</Button>

				{!isUserRegistered && (
					<p className="text-center text-muted mt-3 mb-0" style={{ fontSize: '0.8125rem' }}>
						Already have an account? Just enter your email above.
					</p>
				)}
			</Form>
		</div>
	);
};

// ─── Step 2: Confirm registration ──────────────────────────────────────────
const ConfirmStep = ({ event, onConfirm, loading }) => {
	const sessionDate = moment(event.sessionDatetime);
	return (
		<div className="enrollment-step">
			<div className="step-header mb-4 text-center">
				<div className="step-icon-wrapper mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #1d9eb0 0%, #F68521 100%)' }}>
					<i className="fe fe-calendar step-icon"></i>
				</div>
				<h4 className="step-title">Confirm Registration</h4>
				<p className="step-subtitle text-muted">You're one step away from securing your spot</p>
			</div>

			<div className="erm-event-summary mb-4">
				<p className="erm-event-type">{formatTypeLabel(event.eventType)}</p>
				<h5 className="erm-event-title">{event.title}</h5>
				<div className="erm-details-list mt-3">
					<div className="erm-detail-item">
						<i className="fe fe-calendar me-2"></i>
						{sessionDate.format('dddd, MMMM D, YYYY')}
					</div>
					<div className="erm-detail-item">
						<i className="fe fe-clock me-2"></i>
						{sessionDate.format('h:mm A')}
						{event.durationMinutes > 0 && (
							<span className="text-muted ms-1">· {event.durationMinutes} min</span>
						)}
					</div>
					{event.speakerName && (
						<div className="erm-detail-item">
							<i className="fe fe-mic me-2"></i>
							{event.speakerName}
							{event.speakerTitle && (
								<span className="text-muted">, {event.speakerTitle}</span>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="erm-note mb-4">
				<i className="fe fe-mail me-2"></i>
				A join link will be sent to your email after registration.
			</div>

			<Button className="btn-enrollment-primary w-100" onClick={onConfirm} disabled={loading}>
				{loading ? (
					<><span className="spinner-border spinner-border-sm me-2"></span>Registering...</>
				) : (
					<><i className="fe fe-check me-2"></i>Confirm Registration</>
				)}
			</Button>
		</div>
	);
};

// ─── Main modal ────────────────────────────────────────────────────────────
const EventRegisterModal = ({ show, onHide, event, onConfirm, loading }) => {
	const { isAuthenticated } = useAuth();
	// Skip auth step if already logged in
	const [step, setStep] = useState(isAuthenticated ? 2 : 1);

	// Reset step when modal opens/closes
	React.useEffect(() => {
		if (show) setStep(isAuthenticated ? 2 : 1);
	}, [show, isAuthenticated]);

	if (!event) return null;

	const steps = isAuthenticated ? [{ label: 'Confirm', icon: 'fe-check' }]
		: [
			{ label: 'Account', icon: 'fe-user' },
			{ label: 'Confirm', icon: 'fe-check' },
		];

	const currentStepIndex = isAuthenticated ? 0 : step - 1;

	return (
		<Modal show={show} onHide={onHide} centered size="lg" className="enrollment-modal">
			<Modal.Header closeButton className="enrollment-modal-header">
				<div className="enrollment-progress-container w-100">
					<div className="enrollment-progress-steps">
						{steps.map((s, i) => (
							<div
								key={i}
								className={`progress-step ${currentStepIndex >= i ? 'active' : ''} ${currentStepIndex > i ? 'completed' : ''}`}
							>
								<div className="step-indicator">
									{currentStepIndex > i
										? <i className="fe fe-check"></i>
										: <i className={`fe ${s.icon}`}></i>}
								</div>
								<span className="step-label">{s.label}</span>
							</div>
						))}
					</div>
				</div>
			</Modal.Header>

			<Modal.Body className="enrollment-modal-body">
				{step === 1 && !isAuthenticated && (
					<AuthStep onAuthenticated={() => setStep(2)} />
				)}
				{(step === 2 || isAuthenticated) && (
					<ConfirmStep event={event} onConfirm={onConfirm} loading={loading} />
				)}
			</Modal.Body>

			<style>{`
				.enrollment-modal .modal-content {
					border: none;
					border-radius: 24px;
					overflow: hidden;
					box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
				}
				.enrollment-modal-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					border-bottom: 2px solid #e2e8f0;
					padding: 1.5rem;
				}
				.enrollment-progress-container { width: 100%; }
				.enrollment-progress-steps {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 2rem;
				}
				.progress-step {
					display: flex;
					align-items: center;
					gap: 0.5rem;
					color: #94a3b8;
				}
				.progress-step.active { color: #F68521; }
				.progress-step.completed { color: #10b981; }
				.step-indicator {
					width: 36px;
					height: 36px;
					border-radius: 50%;
					background: #e2e8f0;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 0.875rem;
					transition: all 0.3s ease;
				}
				.progress-step.active .step-indicator {
					background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
					color: white;
					box-shadow: 0 4px 12px rgba(246,133,33,0.3);
				}
				.progress-step.completed .step-indicator {
					background: #10b981;
					color: white;
				}
				.step-label {
					font-size: 0.8125rem;
					font-weight: 600;
				}
				.enrollment-modal-body { padding: 2rem; }

				/* Auth / Confirm step shared styles */
				.step-icon-wrapper {
					width: 80px;
					height: 80px;
					border-radius: 50%;
					background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
					display: flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 8px 20px rgba(246,133,33,0.3);
				}
				.step-icon { font-size: 2rem; color: white; }
				.step-title { font-size: 1.5rem; font-weight: 700; color: #1e293b; }
				.step-subtitle { font-size: 0.9375rem; }
				.form-label-modern { font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 0.375rem; }
				.form-control-modern {
					border: 2px solid #e2e8f0;
					border-radius: 10px;
					padding: 0.625rem 1rem;
					font-size: 0.9375rem;
					transition: border-color 0.2s;
				}
				.form-control-modern:focus { border-color: #F68521; box-shadow: 0 0 0 3px rgba(246,133,33,0.1); }
				.privacy-notice {
					background: #f8fafc;
					border-radius: 8px;
					padding: 0.75rem 1rem;
					display: flex;
					align-items: flex-start;
				}
				.btn-enrollment-primary {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					color: white;
					font-weight: 600;
					font-size: 1rem;
					padding: 0.75rem;
					border-radius: 12px;
					transition: opacity 0.2s;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.btn-enrollment-primary:hover:not(:disabled) { opacity: 0.9; color: white; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); }
				.btn-enrollment-primary:disabled { opacity: 0.65; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); color: white; }

				/* Confirm step */
				.erm-event-summary {
					background: #f8fafc;
					border-radius: 12px;
					border: 1px solid #e2e8f0;
					padding: 1.25rem;
				}
				.erm-event-type {
					font-size: 0.75rem;
					font-weight: 700;
					color: #F68521;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					margin-bottom: 0.35rem;
				}
				.erm-event-title { font-size: 1rem; font-weight: 700; color: #1e293b; margin: 0; }
				.erm-details-list { display: flex; flex-direction: column; gap: 0.5rem; }
				.erm-detail-item { font-size: 0.875rem; color: #475569; display: flex; align-items: center; }
				.erm-note {
					font-size: 0.8125rem;
					color: #64748b;
					background: rgba(29,158,176,0.06);
					border-left: 3px solid #1d9eb0;
					padding: 0.75rem 1rem;
					border-radius: 0 8px 8px 0;
					display: flex;
					align-items: center;
				}
				.spinner-border { width: 1rem; height: 1rem; border-width: 2px; }
			`}</style>
		</Modal>
	);
};

export default EventRegisterModal;
